import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { useQuery } from "@tanstack/react-query";
import { ChevronLeftIcon, ChevronRightIcon, FileIcon } from "lucide-react";

import { initUseQuery } from "simplydo/core/query";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Container } from "simplydo/react/ui/container";
import { Icon } from "simplydo/react/ui/icon";
import { Message, MessageHeader } from "simplydo/react/ui/message";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import IdeaBody from "components/ideas/Body";
import IdeaHeader from "components/ideas/Header";
import { IdeaProvider } from "components/ideas/IdeaProvider";
import IdeaStatusBar from "components/ideas/StatusBar";
import { useAssessmentEditor } from "components/ideas/hooks/useAssessmentEditor";
import { useIdea } from "components/ideas/hooks/useIdea";
import { HoverAction, HoverActions } from "components/lib/HoverActions";
import SlidingModal from "components/lib/SlidingModal";

import PreviewSideWindow from "./PreviewSideWindow/PreviewSideWindow";
import type {
  HandledIdeaPreviewProps,
  IdeaPreviewChallenge,
  IdeaPreviewIdea,
  IdeaPreviewProps,
} from "./types";

export type { HandledIdeaPreviewProps, IdeaPreviewProps } from "./types";

const noopUpdateProjectLane = (
  _ideaIds: string[],
  _laneId: string,
  _order?: number,
) => {};

const ModalContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  min-width: 0;
  max-width: 100%;
`;

const PreviewContainer = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: 100%;
  background-color: #f2f2f3;
`;

/** Full-height sticky parent — PreviewContainer's height:100% would end sticky early. */
const PreviewMain = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

const PreviewHeaderWrap = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const IdeaContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 13;
  background-color: #f2f2f3;
  width: 100%;
  max-width: 100%;
  min-width: 0;
`;

const BodyContainer = styled.div`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  /* clip, not hidden: hidden makes this a scroll container and breaks sticky children */
  overflow-x: clip;
  padding: ${({ theme }) => (theme.sizes.isComputer ? "1rem" : "12px")};
`;

type IdeaPreviewComponentProps = IdeaPreviewProps;

const IdeaPreview = ({
  isOpen,

  challenge: propChallenge,
  onCloseModal = () => {},
  onAfterCloseModal,

  moveIdea,
  canGoNextIdea,
  canGoPrevIdea,
  assessmentEnabled,
  updateProjectLane = noopUpdateProjectLane,
  removeIdeaFromMerge,
  removingIdeaFromMerge,
  currentIdeaPosition,
  totalIdeas,
  redirectToBoard,
  page = 1,
  limit = 0,
}: IdeaPreviewComponentProps) => {
  const user = useAppSelector((state) => state.user);
  const navigate = useNavigate();
  const [idea, ideaQuery] = useIdea();
  const loading = ideaQuery.isLoading;

  const previewContainerRef = useRef<HTMLDivElement>(null);
  const [modalIdeaWidth, setModalIdeaWidth] = useState(0);
  const [commentRef, setCommentRef] = useState<HTMLElement | null>(null);
  const location = useLocation();
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const setSectionRef = (ref: HTMLDivElement | null, itemId: string) => {
    sectionRefs.current[itemId] = ref;

    if (itemId === "comments") {
      setCommentRef(ref);
    }
  };
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const setFieldRef = (ref: HTMLDivElement | null, itemId: string) => {
    fieldRefs.current[itemId] = ref;
  };

  const { t } = useTranslation();

  const theme = useTheme();
  const challengeId = idea?.challenge;
  const board = useMemo(
    () => idea?.projectManagement?.boards?.find((b) => b.forId === challengeId),
    [idea, challengeId],
  );
  const ideaId = idea?._id;
  const userId = user?._id;

  const { clearAssessmentRound } = useAssessmentEditor(idea);

  // only show pm board management actions if the user can manage challenge (collaborator/owner/admins) or is assigned to the idea
  const canManageChallenge = util.canManageChallenge(
    user!,
    propChallenge ?? idea?.ownerChallenge,
  );
  const canViewProjectBoard =
    canManageChallenge ||
    ((board?.assignees || []).indexOf(user?._id ?? "") > -1 &&
      idea?.ownerChallenge?.projectManagementVisibility === "users") ||
    util.hasPermission(user!, "challenge.viewProjectBoard", challengeId ?? "");

  const handleCloseModal = useCallback(() => {
    setModalIdeaWidth(0);
    clearAssessmentRound();
    onCloseModal();
  }, [clearAssessmentRound, onCloseModal]);

  const updateIdea = useCallback(
    (data: unknown, path: string[]) => {
      util.onUpdateIdea(idea, data, path);
    },
    [idea],
  );

  const onSubmit = useCallback(
    (isSubmitted: boolean) => {
      util.onUpdateIdea(idea, isSubmitted, ["isSubmitted"]);
    },
    [idea],
  );

  const setEditing = useCallback(
    (nextEditing: boolean) => {
      if (nextEditing && ideaId) {
        navigate(`/ideas/${ideaId}`);
      }
    },
    [ideaId, navigate],
  );

  const deleteIdea = useCallback(() => {
    util
      .confirm(t("ideas.delete.title"), t("ideas.delete.info"))
      .then(() => {
        if (!ideaId) return;
        api.ideas.delete(
          ideaId,
          () => {
            api.invalidateQueries(api.ideas.get, [ideaId]);
            api.invalidateQueries(api.challenges.get, [challengeId]);
            api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
            api.invalidateQueries(api.challenges.getIdeaImpacts, [challengeId]);
            toast(
              t("generic.typeDeleted", {
                type: t("common:capitalise", { key: "generic.idea" }),
              }),
            );
            handleCloseModal();
          },
          (err) => toast.error(err.message),
        );
      })
      .catch(() => {});
  }, [ideaId, t, challengeId, handleCloseModal]);

  const leaveIdea = useCallback(() => {
    util
      .confirm(
        t("ideas.teammates.leave.title"),
        t("ideas.teammates.leave.info"),
      )
      .then(() => {
        if (!ideaId || !userId) {
          return;
        }
        api.ideas.removeCollaborator(
          ideaId,
          userId,
          ({ authors, collaborators }) => {
            util.onUpdateIdea(idea, authors, ["authors"]);
            util.onUpdateIdea(idea, collaborators, ["collaborators"]);
            ideaQuery.refetch();
          },
          (err) => toast.error(err.message),
        );
      })
      .catch(() => {});
  }, [ideaId, userId, t, idea, ideaQuery]);

  const onOpenPreview = useCallback(() => {
    const rect = previewContainerRef.current?.getBoundingClientRect();
    setModalIdeaWidth(rect?.width ?? 0);
  }, []);

  useEffect(() => {
    if (idea) {
      api.setQueryData(api.challenges.getIdeas, [idea.challenge], (prev) => ({
        ...prev,
        ideas: (prev.ideas || []).map((oldIdea) => {
          if (oldIdea._id !== idea._id) return oldIdea;
          return { ...oldIdea, currentUserHasViewed: true };
        }),
      }));
    }
  }, [idea]);

  useEffect(() => {
    if (commentRef && location.hash === "#comments") {
      commentRef.scrollIntoView({ behavior: "smooth", block: "start" });
      navigate(location.search);
    }
  }, [commentRef, location.hash, location.search, navigate]);

  return (
    <SlidingModal
      isOpen={isOpen}
      onOpen={onOpenPreview}
      onClose={handleCloseModal}
      onAfterClose={onAfterCloseModal}
      width={theme?.sizes.isComputer ? "60%" : "100%"}
      ref={previewContainerRef}
    >
      <ModalContainer>
        {theme.sizes.isComputer && modalIdeaWidth > 0 ? (
          <HoverActions $width={modalIdeaWidth}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <HoverAction onClick={() => handleCloseModal()}>
                Close
              </HoverAction>
              {moveIdea && isOpen && (canGoPrevIdea || canGoNextIdea) ? (
                <div style={{ display: "flex", gap: 8 }}>
                  <Popup
                    on="hover"
                    trigger={
                      <div>
                        <HoverAction
                          onClick={() => moveIdea("prev")}
                          margined={!(currentIdeaPosition && totalIdeas)}
                          disabled={!canGoPrevIdea}
                        >
                          <Icon icon={ChevronLeftIcon} />
                        </HoverAction>
                      </div>
                    }
                  >{`Go to previous ${t("generic.idea")}`}</Popup>

                  {currentIdeaPosition &&
                    totalIdeas &&
                    (canGoNextIdea || canGoPrevIdea) && (
                      <HoverAction disabled>
                        {t("common:capitalise", { key: "generic.idea" })}{" "}
                        {(page - 1) * limit + currentIdeaPosition} of{" "}
                        {totalIdeas}
                      </HoverAction>
                    )}
                  <Popup
                    trigger={
                      <div>
                        <HoverAction
                          onClick={() => moveIdea("next")}
                          disabled={!canGoNextIdea}
                        >
                          <Icon icon={ChevronRightIcon} />
                        </HoverAction>
                      </div>
                    }
                  >{`Go to next ${t("generic.idea")}`}</Popup>
                </div>
              ) : null}
            </div>

            <div className="hover-actions-inner">
              <PreviewSideWindow
                assessmentEnabled={assessmentEnabled}
                updateProjectLane={updateProjectLane}
                challenge={propChallenge as IdeaPreviewChallenge | undefined}
                sectionRefs={sectionRefs}
                fieldRefs={fieldRefs}
                scrollRef={previewContainerRef}
              />
            </div>
          </HoverActions>
        ) : null}
        <PreviewContainer>
          {!loading && !idea ? (
            <Container style={{ height: "100vh" }}>
              <Message color="red" style={{ margin: "2rem 0" }}>
                <MessageHeader>
                  Unable to load {t("generic.idea")}
                </MessageHeader>
                <p>
                  Sorry, we were unable to load this {t("generic.idea")}. Please
                  try again later.
                </p>
              </Message>
              <Button onClick={() => handleCloseModal()}>
                <Icon icon={ChevronLeftIcon} />
                {`Close`}
              </Button>
            </Container>
          ) : (
            <>
              <PreviewMain>
                <IdeaStatusBar
                  stickyTop={0}
                  isSaving={false}
                  unsaved={false}
                  isEditing={false}
                  setEditing={setEditing}
                  onSubmit={onSubmit}
                  otherUsers={{}}
                  websocketConnected={true}
                  updateIdea={updateIdea}
                  deleteIdea={deleteIdea}
                  leaveIdea={leaveIdea}
                  previewFullIdeaHref={ideaId ? `/ideas/${ideaId}` : undefined}
                  onClosePreview={handleCloseModal}
                />

                <PreviewHeaderWrap>
                  <IdeaHeader isEditing={false} updateIdea={updateIdea} />
                </PreviewHeaderWrap>

                <IdeaContainer>
                  <BodyContainer>
                    {!theme.sizes.isComputer &&
                    idea &&
                    !loading &&
                    canViewProjectBoard ? (
                      <PreviewSideWindow
                        assessmentEnabled={assessmentEnabled}
                        updateProjectLane={updateProjectLane}
                        challenge={
                          propChallenge as IdeaPreviewChallenge | undefined
                        }
                        fieldRefs={fieldRefs}
                        sectionRefs={sectionRefs}
                        scrollRef={previewContainerRef}
                      />
                    ) : null}
                    <IdeaBody
                      key={idea?._id ?? ""}
                      idea={idea}
                      setSectionRef={setSectionRef}
                      sectionRefs={sectionRefs}
                      setFieldRef={setFieldRef}
                      fieldRefs={fieldRefs}
                      loading={loading}
                      isEditing={false}
                      scrollRef={previewContainerRef}
                      removeIdeaFromMerge={removeIdeaFromMerge}
                      removingIdeaFromMerge={removingIdeaFromMerge}
                      updateIdea={(value, path) => {
                        util.onUpdateIdea(idea, value, path);
                      }}
                    />
                  </BodyContainer>
                </IdeaContainer>
              </PreviewMain>
            </>
          )}
        </PreviewContainer>
      </ModalContainer>
    </SlidingModal>
  );
};

type HandledIdeaPreviewComponentProps = HandledIdeaPreviewProps;

export const HandledIdeaPreview = ({
  ideaId: propIdeaId,
  removeIdeaFromMerge: propRemoveIdeaFromMerge,
  updateProjectLane: propUpdateProjectLane,
  ...rest
}: HandledIdeaPreviewComponentProps) => {
  const [ideaId, setIdeaId] = useState<string | null>(propIdeaId);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!propIdeaId) {
      setIsOpen(false);
      return;
    }
    setIsOpen(true);
    setIdeaId(propIdeaId);
  }, [propIdeaId]);

  const ideaQuery = useQuery(
    initUseQuery(api.ideas.get, [ideaId!], {
      isEnabled: !!ideaId,
      select: (data) => ({ _id: data._id, challenge: data.challenge }),
    }),
  );

  const removeIdeaFromMerge = useCallback(
    (mergeIdeaId: string, childIdeaId: string) => {
      propRemoveIdeaFromMerge?.(mergeIdeaId, childIdeaId, () => {
        if (mergeIdeaId === ideaId && ideaId) {
          api.setQueryData(api.ideas.get, [ideaId], (prev) => {
            if (!prev || typeof prev !== "object") return prev;
            const ideaData = prev as IdeaPreviewIdea;
            return {
              ...(ideaData as object),
              children: (ideaData.children ?? []).filter(
                (c: string) => c !== childIdeaId,
              ),
              ownerChildren: (ideaData.ownerChildren ?? []).filter(
                (c) => c._id !== childIdeaId,
              ),
            } as IdeaPreviewIdea;
          });
        }
      });
    },
    [ideaId, propRemoveIdeaFromMerge],
  );

  const updateProjectLane = useCallback(
    (movingIdeaIds: string[], laneId: string, order: number = 0) => {
      const challengeId = ideaQuery.data?.challenge;
      if (!challengeId || !movingIdeaIds.length || !laneId) {
        return;
      }

      api.boards.moveProjectIdeas(
        challengeId,
        {
          ideaIds: movingIdeaIds,
          targetLane: laneId,
          order,
          sendAnnouncements: [],
        },
        ({ updatedIdeas }) => {
          api.setQueryData(api.challenges.getIdeas, [challengeId], (prev) => {
            if (!prev?.ideas || !updatedIdeas) return prev;

            return {
              ...prev,
              ideas: prev.ideas.map((idea) => {
                const matchingUpdatedIdea = updatedIdeas.find(
                  (updated) => updated._id === idea._id,
                );
                if (!matchingUpdatedIdea) return idea;
                return {
                  ...idea,
                  projectManagement: matchingUpdatedIdea.projectManagement,
                };
              }),
            };
          });
        },
        () => {},
      );
    },
    [ideaQuery.data?.challenge],
  );

  return (
    <IdeaProvider ideaId={ideaId ?? undefined}>
      <IdeaPreview
        {...rest}
        isOpen={isOpen}
        removeIdeaFromMerge={removeIdeaFromMerge}
        updateProjectLane={propUpdateProjectLane ?? updateProjectLane}
        onAfterCloseModal={() => setIdeaId(null)}
      />
    </IdeaProvider>
  );
};

export default IdeaPreview;
